/**
 * Drawer.tsx
 * Phase 1n Feedback Layer — Sheet wrapper (side="right")
 * Issue #3013
 *
 * Tier 1 Scope:
 * - side="right" 固定
 * - フィルタ等のサイドパネル用途
 * - reduced-motion 対応
 *
 * 禁止事項:
 * - components/ui/sheet.tsx の書換禁止
 */

import * as React from 'react';
import { cn } from '@/lib/utils';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DrawerProps {
  /** 表示状態 */
  open: boolean;
  /** 閉じるコールバック */
  onOpenChange: (open: boolean) => void;
  /** タイトル */
  title?: React.ReactNode;
  /** 説明文 */
  description?: React.ReactNode;
  /** フッターコンテンツ (適用・リセットボタン等) */
  footer?: React.ReactNode;
  /** メインコンテンツ */
  children?: React.ReactNode;
  /** 幅クラス (default: 'w-3/4 sm:max-w-sm') */
  widthClassName?: string;
  /** 追加の className */
  className?: string;
}

// ---------------------------------------------------------------------------
// Drawer component
// ---------------------------------------------------------------------------

/**
 * Drawer
 *
 * shadcn/ui Sheet (side="right") の wrapper。
 * フィルタパネル等、右側から開くサイドパネル用途。
 *
 * @example
 * ```tsx
 * <Drawer
 *   open={filterOpen}
 *   onOpenChange={setFilterOpen}
 *   title="予約フィルター"
 *   description="表示する予約を絞り込みます"
 *   footer={
 *     <>
 *       <Button variant="outline" onClick={handleReset}>リセット</Button>
 *       <Button onClick={handleApply}>適用</Button>
 *     </>
 *   }
 * >
 *   <FilterForm />
 * </Drawer>
 * ```
 */
export function Drawer({
  open,
  onOpenChange,
  title,
  description,
  footer,
  children,
  widthClassName,
  className,
}: DrawerProps): React.ReactElement {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className={cn(widthClassName, className)}>
        {(title || description) && (
          <SheetHeader>
            {title && <SheetTitle>{title}</SheetTitle>}
            {description && <SheetDescription>{description}</SheetDescription>}
          </SheetHeader>
        )}
        <div className="flex-1 overflow-y-auto">{children}</div>
        {footer && <SheetFooter>{footer}</SheetFooter>}
      </SheetContent>
    </Sheet>
  );
}
